import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { Select, TextArea, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyGetEmailTemplateQuery } from "services/email-api.ts";
import { useSendServiceRequestForMaterialsEmailMutation } from "services/service-api.ts";
import { extractErrorMessage } from "utils/error-utils";

import EmailAddressMultiSelect from "./EmailAddressMultiSelect";

const REQUEST_FOR_MATERIALS_EMAIL_TYPE = "request_for_materials";

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export type RequestMaterialsServiceOption = {
  id: string;
  type: string;
  label: string;
};

type SelectedService = {
  id: string;
  type: string;
};

type RequestMaterialsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId: string;
  defaultToEmail?: string | null;
  serviceId?: string;
  serviceType?: string;
  services?: RequestMaterialsServiceOption[];
};

const RequestMaterialsModal = ({
  isOpen,
  onClose,
  fileId,
  defaultToEmail,
  serviceId,
  serviceType,
  services,
}: RequestMaterialsModalProps) => {
  const preselectedService: SelectedService | undefined =
    serviceId && serviceType ? { id: serviceId, type: serviceType } : undefined;
  const singleFileViewService: SelectedService | undefined =
    !preselectedService && services?.length === 1
      ? { id: services[0].id, type: services[0].type }
      : undefined;
  const knownService = preselectedService ?? singleFileViewService;
  const showServicePicker = !knownService && Boolean(services?.length);

  const [toEmail, setToEmail] = useState(defaultToEmail ?? "");
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [pickedServiceId, setPickedServiceId] = useState("");

  const selectedService: SelectedService | undefined =
    knownService ?? services?.find((s) => s.id === pickedServiceId);

  const hasEditedSubjectRef = useRef(false);
  const hasEditedBodyRef = useRef(false);

  const [fetchTemplate, { isFetching: isTemplateLoading }] =
    useLazyGetEmailTemplateQuery();
  const [sendEmail, { isLoading: isSending }] =
    useSendServiceRequestForMaterialsEmailMutation();

  const loadTemplate = useCallback(
    async (service: SelectedService | undefined) => {
      if (!service) {
        return;
      }
      try {
        const template = await fetchTemplate({
          emailType: REQUEST_FOR_MATERIALS_EMAIL_TYPE,
          fileId,
          serviceType: service.type,
        }).unwrap();

        if (!hasEditedBodyRef.current) {
          setBody(template.body);
        }
        if (!hasEditedSubjectRef.current) {
          setSubject(template.subject);
        }
      } catch (error) {
        console.error(error);
        KaboodleToaster.error(
          extractErrorMessage(error, "Failed to load the email template."),
        );
      }
    },
    [fetchTemplate, fileId],
  );

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justOpened) {
      hasEditedSubjectRef.current = false;
      hasEditedBodyRef.current = false;
      setToEmail(defaultToEmail ?? "");
      setCcEmails([]);
      setSubject("");
      setBody("");
      setPickedServiceId("");
      if (knownService) {
        loadTemplate(knownService);
      }
    }
    // Intentionally edge-triggered on isOpen alone; see justOpened above.
  }, [isOpen]);

  const handleServiceChange = (option: OptionType | null) => {
    hasEditedSubjectRef.current = false;
    hasEditedBodyRef.current = false;
    setSubject("");
    setBody("");
    const nextServiceId = option?.value ? String(option.value) : "";
    setPickedServiceId(nextServiceId);
    loadTemplate(services?.find((s) => s.id === nextServiceId));
  };

  const ccHasInvalidEmail = ccEmails.some(
    (email) => !EMAIL_PATTERN.test(email),
  );
  const hasInvalidToEmail = toEmail !== "" && !EMAIL_PATTERN.test(toEmail);

  const handleSend = async () => {
    if (
      !selectedService ||
      !toEmail ||
      hasInvalidToEmail ||
      ccHasInvalidEmail ||
      !subject ||
      !body
    ) {
      return;
    }

    try {
      await sendEmail({
        id: selectedService.id,
        to_email: toEmail,
        subject,
        body,
        cc_emails: ccEmails,
      }).unwrap();
      KaboodleToaster.success("Request for materials email sent.");
      onClose();
    } catch (error) {
      console.error(error);
      KaboodleToaster.error(
        extractErrorMessage(
          error,
          "Failed to send request for materials email. Please try again.",
        ),
      );
    }
  };

  const serviceOptions: OptionType[] =
    services?.map((service) => ({
      label: service.label,
      value: service.id,
    })) ?? [];
  const selectedServiceOption =
    serviceOptions.find((option) => option.value === pickedServiceId) ?? null;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleSend}
            disabled={
              !selectedService ||
              !toEmail ||
              hasInvalidToEmail ||
              ccHasInvalidEmail ||
              !subject ||
              !body ||
              isSending ||
              isTemplateLoading
            }
          >
            {isSending ? "Sending..." : "Send"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Request For Materials Email">
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          {showServicePicker && (
            <Select
              name="requestMaterialsService"
              label="Service"
              value={selectedServiceOption}
              options={serviceOptions}
              onChange={(option) => handleServiceChange(option as OptionType)}
              isClearable={false}
            />
          )}
          <TextInput
            name="requestMaterialsToEmail"
            label="Recipient Email"
            autoComplete="new-password"
            value={toEmail}
            onChange={(e) => setToEmail(e.target.value)}
            required
          />
          {hasInvalidToEmail && (
            <div className="error-message">Enter a valid recipient email.</div>
          )}
          <EmailAddressMultiSelect
            name="requestMaterialsCcEmails"
            label="CC"
            value={ccEmails}
            onChange={setCcEmails}
          />
          <TextInput
            name="requestMaterialsSubject"
            label="Subject"
            autoComplete="new-password"
            value={isTemplateLoading ? "Loading..." : subject}
            readOnly={isTemplateLoading}
            onChange={(e) => {
              hasEditedSubjectRef.current = true;
              setSubject(e.target.value);
            }}
            required
          />
          <TextArea
            name="requestMaterialsBody"
            label="Body"
            value={isTemplateLoading ? "Loading..." : body}
            readOnly={isTemplateLoading}
            onChange={(e) => {
              hasEditedBodyRef.current = true;
              setBody(typeof e === "string" ? e : e.target.value);
            }}
            rows={16}
            required
          />
        </form>
      </Card>
    </KaboodleModal>
  );
};

export default RequestMaterialsModal;
